import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  Link,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Lock,
  Video,
  Clock,
  Copy,
  Check,
  CalendarClock,
  XCircle,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import {
  useInterviewInvite,
  useAcknowledgeInterview,
} from 'src/hooks/useInterviewInvite';
import './InterviewInvite.css';
import {
  mapInvite,
  derivePhase,
  isScheduledPhase,
  headlineFor,
  formatWhen,
  formatShort,
  formatDay,
  until,
} from './interviewInvite.helpers';

// Demo payload (same shape as the GET response) so the placeholder UI renders
// with no `?token=` in development. Production with no token → invalid link.
const DEMO_DATA = {
  candidate_name: 'SAI KIRAN',
  job_title: 'Compliance Manager',
  organization: 'Visa',
  round: 'Round 1',
  interviewers: ['Christo Joseph'],
  format: 'Microsoft Teams video call',
  start_time: new Date(Date.now() + 20 * 60000).toISOString(),
  end_time: new Date(Date.now() + 80 * 60000).toISOString(),
  time_zone: 'UTC',
  status: 'scheduled',
  candidate_acknowedged: false,
  candidate_rejected: false,
  requires_acknowledgment: false,
  join_available: false,
  join_url: null,
  unlock_minutes_before: 30,
  grace_minutes_after: 20,
  support_email: 'ta@ahedge.in',
  reference: 'R0508260099-R1',
};
const IS_DEV = process.env.NODE_ENV !== 'production';

// Confirms "I can't attend" before it fires — there's no in-app way back to
// the "Yes, I'll be there" state once the server records a decline.
function ConfirmDeclineDialog({ onCancel, onConfirm, pending }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Portaled to <body> so it's never at the mercy of an ancestor's transform
  // or contain (e.g. an entrance animation on a parent `.ii-card`/section) —
  // any of those silently turn `position: fixed` into "fixed to that
  // ancestor" instead of the viewport. Re-wrapped in `.ii-root` because the
  // --ii-* design tokens are only defined on that class — outside it
  // `var(--ii-surface)` etc. resolve to nothing. The wrapper has no visible
  // content of its own (its only child is `position: fixed`), so it adds no
  // layout footprint.
  return createPortal(
    <div className="ii-root">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="ii-confirm-overlay"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) onCancel();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        tabIndex={-1}
      >
        <div
          className="ii-confirm-card"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="ii-confirm-title"
          aria-describedby="ii-confirm-desc"
        >
          <h3 id="ii-confirm-title">Can&apos;t make this interview?</h3>
          <p id="ii-confirm-desc">
            We&apos;ll let the team know you won&apos;t be there. If this was a
            mis-tap, you can confirm your slot instead.
          </p>
          <div className="ii-confirm-actions">
            <button
              ref={cancelRef}
              className="ii-btn ii-btn-join"
              type="button"
              onClick={onCancel}
            >
              Actually, I&apos;ll be there
            </button>
            <button
              className="ii-btn ii-btn-ghost"
              type="button"
              onClick={onConfirm}
              disabled={pending}
            >
              {pending ? 'Sending…' : "No, I can't attend"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

ConfirmDeclineDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  pending: PropTypes.bool.isRequired,
};

function Shell({ children }) {
  return (
    <div className="ii-root">
      <div className="ii-wrap">{children}</div>
    </div>
  );
}
Shell.propTypes = { children: PropTypes.node.isRequired };

function LoadingSkeleton() {
  return (
    <Shell>
      <div className="ii-skel ii-skel-title" />
      <div className="ii-skel ii-skel-role" />
      <div className="ii-card" aria-hidden="true">
        <div className="ii-when">
          <div className="ii-skel ii-skel-date" />
          <div className="ii-skel ii-skel-time" />
        </div>
        <div className="ii-facts">
          <div className="ii-skel ii-skel-line" />
          <div className="ii-skel ii-skel-line" />
          <div className="ii-skel ii-skel-line" />
          <div className="ii-skel ii-skel-line" />
        </div>
        <div className="ii-join">
          <div className="ii-skel ii-skel-btn" />
        </div>
      </div>
    </Shell>
  );
}

function InvalidLink({ protectedRoute, message }) {
  return (
    <Shell>
      <div className="ii-invalid">
        <h1>
          {protectedRoute
            ? "This interview isn't in your pipeline"
            : 'This link is no longer valid'}
        </h1>
        <p>
          {message ||
            (protectedRoute
              ? "It may have closed, or you don't have an interview scheduled for this role."
              : 'The interview link may have expired or already been used. Please check your email for the latest invite, or contact your recruiter.')}
        </p>
        {protectedRoute && (
          <p style={{ marginTop: 12 }}>
            <Link to="/my-applications">← Back to My Applications</Link>
          </p>
        )}
      </div>
    </Shell>
  );
}
InvalidLink.propTypes = {
  protectedRoute: PropTypes.bool,
  message: PropTypes.string,
};
InvalidLink.defaultProps = {
  protectedRoute: false,
  message: '',
};

export default function InterviewInvite() {
  // Reached either via a magic link (`?token=`) or, authenticated, from the
  // "My Applications" list (`/my-applications/interviews/:secMappingId`) —
  // the latter carries no token and hits the protected mirror endpoints.
  const { secMappingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const isProtectedRoute = !!secMappingId;
  const scope = isProtectedRoute ? { secMappingId } : { token };
  const hasScope = !!(token || isProtectedRoute);

  const { data, isLoading, isError, error, refetch } =
    useInterviewInvite(scope);

  // No scope in dev → preview with demo data; in prod → invalid link.
  const raw = data ?? (!hasScope && IS_DEV ? DEMO_DATA : null);
  const invite = useMemo(() => mapInvite(raw), [raw]);

  // Protected mode only: push "My Applications > <role>" up to the app
  // header (same mechanism OpeningDetails.jsx uses), since this view no
  // longer owns the whole page the way the magic-link version does.
  const outletContext = useOutletContext();
  useEffect(() => {
    if (!isProtectedRoute || !outletContext?.setHeaderBreadcrumbs) {
      return undefined;
    }
    outletContext.setHeaderBreadcrumbs([
      { label: 'My Applications', href: '/my-applications' },
      { label: invite?.jobTitle || 'Interview details' },
    ]);
    return () => outletContext.setHeaderBreadcrumbs?.(null);
  }, [isProtectedRoute, invite?.jobTitle, outletContext]);

  // Tick every second so the gate + countdown update on their own.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const phase = invite ? derivePhase(invite, now) : 'invalid';

  // Inside the window but the server hasn't opened the link yet — re-poll so the
  // join button appears without a manual refresh.
  useEffect(() => {
    if (phase !== 'opening' || !hasScope) return undefined;
    const id = setInterval(() => refetch(), 30000);
    return () => clearInterval(id);
  }, [phase, hasScope, refetch]);

  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef(null);
  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  // Attendance confirm / decline. On success the query invalidates → refetches →
  // the UI re-derives from fresh server state (acknowledged or declined).
  const { toast } = useToast();
  const acknowledge = useAcknowledgeInterview(scope);
  const [ackChoice, setAckChoice] = useState(null); // 'yes' | 'no' while pending
  const ackPending = acknowledge.isPending;

  const handleAck = async (isAccepted) => {
    setAckChoice(isAccepted ? 'yes' : 'no');
    try {
      await acknowledge.mutateAsync(isAccepted);
    } catch {
      toast({
        message: "We couldn't save that just now. Please try again.",
        variant: 'destructive',
      });
    } finally {
      setAckChoice(null);
    }
  };

  // "I can't attend" goes through a confirmation first — declining has no
  // in-app undo (see ConfirmDeclineDialog above).
  const [confirmDecline, setConfirmDecline] = useState(false);
  const confirmDeclineNow = () => {
    setConfirmDecline(false);
    handleAck(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invite.joinUrl);
      setCopied(true);
      clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(() => setCopied(false), 2200);
    } catch {
      // clipboard unavailable — the URL is still visible to copy manually
    }
  };

  if (hasScope && isLoading) return <LoadingSkeleton />;
  if (!invite || isError || phase === 'invalid') {
    return (
      <InvalidLink
        protectedRoute={isProtectedRoute}
        message={isProtectedRoute ? error?.message : ''}
      />
    );
  }

  const { dateText, timeText, durationText } = formatWhen(
    invite.start,
    invite.end
  );
  const unlockAt = invite.start.getTime() - invite.unlockMins * 60000;
  const scheduled = isScheduledPhase(phase);
  const isReschedule = invite.status === 'rescheduled' && scheduled;
  const needsAck =
    invite.ack.required &&
    !invite.ack.acknowledged &&
    !invite.ack.rejected &&
    scheduled;

  const interviewerLabel =
    invite.interviewers.length > 1 ? 'Interviewers' : 'Interviewer';
  const interviewerText = invite.interviewers.join(', ') || '—';

  let relText = '';
  if (phase === 'locked')
    relText = `Starts in ${until(invite.start.getTime() - now)}`;
  else if (phase === 'opening') relText = 'Starting shortly';
  else if (phase === 'live') relText = 'In progress';

  const urlDisplay = (invite.joinUrl || '').replace(/^https?:\/\//, '');

  let joinButton = null;
  if (phase === 'live') {
    joinButton = (
      <a
        className="ii-btn ii-btn-join"
        href={invite.joinUrl || '#'}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Video aria-hidden="true" />
        Join on Microsoft Teams
      </a>
    );
  } else if (scheduled) {
    joinButton = (
      <div className="ii-btn ii-btn-locked" aria-disabled="true">
        <Lock aria-hidden="true" />
        Join link not active yet
      </div>
    );
  }

  return (
    <Shell>
      <h1 className="ii-title">
        Hi {invite.firstName}, {headlineFor(phase, invite.status)}
      </h1>
      <p className="ii-role">
        {invite.jobTitle}
        {invite.organization ? ` · ${invite.organization}` : ''}
      </p>

      <main className="ii-card">
        {isReschedule && (
          <div className="ii-reschedule">
            <CalendarClock aria-hidden="true" />
            <span>
              Rescheduled
              {invite.rescheduledAt
                ? ` on ${formatDay(invite.rescheduledAt)}`
                : ''}
              . Please note the new time below.
            </span>
          </div>
        )}

        <div className="ii-when">
          <div className="ii-date">{dateText}</div>
          <div className="ii-time">{timeText}</div>
          {isReschedule && invite.previousStart && (
            <div className="ii-prev">
              Previously <s>{formatShort(invite.previousStart)}</s>
            </div>
          )}
          {relText ? <div className="ii-rel">{relText}</div> : null}
        </div>

        <dl className="ii-facts">
          <dt>Round</dt>
          <dd>{invite.round || '—'}</dd>
          <dt>{interviewerLabel}</dt>
          <dd>{interviewerText}</dd>
          <dt>Format</dt>
          <dd>{invite.format || '—'}</dd>
          <dt>Duration</dt>
          <dd>{durationText}</dd>
          {isReschedule && invite.rescheduleReason && (
            <>
              <dt>Reason</dt>
              <dd>{invite.rescheduleReason}</dd>
            </>
          )}
        </dl>

        <div className="ii-join">
          {needsAck ? (
            <div className="ii-ack">
              <p className="ii-ack-lead">
                <b>Can you make this interview?</b> Let us know so we can
                confirm your slot
                {invite.organization ? ` with ${invite.organization}` : ''}.
              </p>
              <div className="ii-ack-actions">
                <button
                  className="ii-btn ii-btn-join"
                  type="button"
                  onClick={() => handleAck(true)}
                  disabled={ackPending}
                >
                  <Check aria-hidden="true" />
                  {ackChoice === 'yes' ? 'Sending…' : "Yes, I'll be there"}
                </button>
                <button
                  className="ii-btn ii-btn-ghost"
                  type="button"
                  onClick={() => setConfirmDecline(true)}
                  disabled={ackPending}
                >
                  {ackChoice === 'no' ? 'Sending…' : "I can't attend"}
                </button>
              </div>
              {confirmDecline && (
                <ConfirmDeclineDialog
                  onCancel={() => setConfirmDecline(false)}
                  onConfirm={confirmDeclineNow}
                  pending={ackPending}
                />
              )}
            </div>
          ) : (
            <>
              {scheduled && invite.ack.acknowledged && (
                <div className="ii-note ii-note-live ii-note-confirmed">
                  <Check className="ii-note-ic" aria-hidden="true" />
                  <p>
                    <b>You&apos;re confirmed.</b> Thanks — we&apos;ve let the
                    team know you&apos;ll be there.
                  </p>
                </div>
              )}

              {phase === 'locked' && (
                <div className="ii-note">
                  <Lock className="ii-note-ic" aria-hidden="true" />
                  <p>
                    The join button turns on{' '}
                    <b>{invite.unlockMins} minutes before</b> your interview.
                    Keep this page or come back closer to the time — it opens in{' '}
                    <span className="ii-countdown">
                      {until(unlockAt - now)}
                    </span>
                    .
                  </p>
                </div>
              )}
              {phase === 'opening' && (
                <div className="ii-note">
                  <Clock className="ii-note-ic" aria-hidden="true" />
                  <p>
                    <b>Almost there.</b> The meeting room opens any moment now —
                    this page will update on its own.
                  </p>
                </div>
              )}
              {phase === 'live' && (
                <div className="ii-note ii-note-live">
                  <span className="ii-dot" aria-hidden="true" />
                  <p>
                    <b>The meeting room is open.</b> Join a few minutes early so
                    you can check your camera and mic.
                  </p>
                </div>
              )}
              {phase === 'done' && (
                <div className="ii-note ii-note-done">
                  <Clock className="ii-note-ic" aria-hidden="true" />
                  <p>
                    This interview has ended. Your recruiter will be in touch
                    with next steps.
                  </p>
                </div>
              )}
              {phase === 'cancelled' && (
                <div className="ii-note ii-note-done">
                  <XCircle className="ii-note-ic" aria-hidden="true" />
                  <p>
                    This interview has been cancelled. Your recruiter will reach
                    out if it&apos;s rescheduled.
                  </p>
                </div>
              )}
              {phase === 'noShow' && (
                <div className="ii-note ii-note-done">
                  <Clock className="ii-note-ic" aria-hidden="true" />
                  <p>
                    This interview was marked as missed. Please contact your
                    recruiter if you&apos;d like to reschedule.
                  </p>
                </div>
              )}
              {phase === 'declined' && (
                <div className="ii-note ii-note-done">
                  <XCircle className="ii-note-ic" aria-hidden="true" />
                  <p>
                    You&apos;ve let us know you can&apos;t attend. Contact your
                    recruiter if anything changes.
                  </p>
                </div>
              )}

              {joinButton}

              {phase === 'live' && invite.joinUrl && (
                <div className="ii-fallback">
                  <p className="ii-fallback-lead">
                    Button not working? Copy the link and paste it into Teams or
                    your browser.
                  </p>
                  <div className="ii-copy-field">
                    <span className="ii-url" title={invite.joinUrl}>
                      {urlDisplay}
                    </span>
                    <button
                      className={`ii-copy-btn${copied ? ' ii-copy-done' : ''}`}
                      type="button"
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <Check aria-hidden="true" />
                      ) : (
                        <Copy aria-hidden="true" />
                      )}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <footer className="ii-foot">
        <p>
          Need to reschedule, or having trouble joining? Email{' '}
          <a href={`mailto:${invite.supportEmail}`}>{invite.supportEmail}</a>.
        </p>
        {invite.reference ? (
          <p className="ii-ref">Reference {invite.reference}</p>
        ) : null}
      </footer>
    </Shell>
  );
}
